import { useActiveBundle } from '@/context/active-bundle';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { TargetTransition, TargetTransitionOptions } from '@/services/router/safe-navigate';
import type { MergedWorkspace } from '@/features/workspaces';
import { useFilterWorkspacesByName, useWorkspaceCount, useSampleWorkspace } from '@/features/workspaces';
import { AppBundle } from '@/utils/app-bundles';
import { navigateExternal } from '@/utils/navigate';
import { workspaceUrlFromCurrentPath } from '@/utils/navigation';
import {
  Avatar,
  Box,
  Button,
  Icon,
  Label,
  List,
  ListItem,
  PlainButton,
  PlainLink,
  PopoverContent,
  Search,
  Text,
  TextLabel,
  Tooltip,
  Truncated,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { useAnalytics } from '@/services/analytics';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import {
  FAST_02_STANDARD_PRODUCTIVE_TRANSITION,
  ENTRANCE_FADE_UP_ANIMATE,
  entranceFadeUpInitial,
  FAST_01_DURATION,
  FAST_02_ENTRANCE_PRODUCTIVE_TRANSITION,
  REDUCED_MOTION_TRANSITION,
} from '@/utils/motion';

import type { Ref } from 'react';
import { use, useMemo, useRef, useState } from 'react';
import { WorkspaceDot } from '../../../workspace-dot';
import * as styles from './index.css';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { useAuth } from '@/services/auth';
import { useStore } from '@nanostores/react';
import {
  changeToSession,
  currentSessionStore,
  type Session,
  sessionsStore,
  updateSessions,
} from '@customerio/hydra-session-state';
import {
  MAX_ACCOUNTS,
  PENDING_SESSION_SWITCH_KEY,
  DECOUPLE_USERS_FLAG,
  AccountSwitcherContext,
  useAccountMemberships,
  switchAccount,
} from '@/features/account-switcher';
import { hydraUiApi, useHydraUIAPIMutation } from '@/services/api';
import { session as authSession } from '@/services/session';

const PANEL_SLIDE_DISTANCE = 160;

const panelVariants = {
  enter: (direction: 1 | -1) => ({
    x: direction > 0 ? PANEL_SLIDE_DISTANCE : -PANEL_SLIDE_DISTANCE,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: FAST_02_STANDARD_PRODUCTIVE_TRANSITION,
  },
  exit: (direction: 1 | -1) => ({
    x: direction > 0 ? -PANEL_SLIDE_DISTANCE : PANEL_SLIDE_DISTANCE,
    opacity: 0,
    transition: FAST_02_STANDARD_PRODUCTIVE_TRANSITION,
  }),
};

function itemStaggerTransition(index: number, shouldReduceMotion: boolean | null) {
  if (shouldReduceMotion === true) {
    return REDUCED_MOTION_TRANSITION;
  }
  return {
    ...FAST_02_ENTRANCE_PRODUCTIVE_TRANSITION,
    delay: Math.min(index, 5) * FAST_01_DURATION,
  };
}

function isWorkspaceActive(
  workspace: MergedWorkspace,
  currentWorkspace: MergedWorkspace | null,
  activeApp: AppBundle | null,
) {
  if (activeApp === AppBundle.Journeys) {
    return workspace.cio_workspace_id === currentWorkspace?.cio_workspace_id;
  }

  return workspace.id === currentWorkspace?.id;
}

function isSessionExpired(session: Session): boolean {
  return session.expires_at < Math.floor(Date.now() / 1000);
}

// Identity-provider display names for the login badge. Password/email logins are
// unlabeled in the design, and any provider not mapped here is title-cased.
const AUTH_PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  saml: 'SAML',
  okta: 'Okta',
  microsoft: 'Microsoft',
};

function formatAuthProvider(provider: string | undefined): string | null {
  if (provider == null || isEmptyString(provider) || provider === 'password' || provider === 'email') {
    return null;
  }
  return AUTH_PROVIDER_LABELS[provider] ?? `${provider.charAt(0).toUpperCase()}${provider.slice(1)}`;
}

function SampleWorkspaceItem() {
  return (
    <Tooltip
      content="Explore Customer.io with sample data — view only, no changes allowed."
      placement="right"
      isCustom={true}
    >
      {(context) => (
        <PlainLink
          href="/sample-workspace"
          className={styles.workspaceItemLink}
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        >
          <div className={styles.workspaceItemNameContainer}>
            <Icon name="a-b-test" size="sm" />
            <Truncated className={styles.workspaceItemName} aria-label="Sample Workspace">
              Sample Workspace
            </Truncated>
          </div>
        </PlainLink>
      )}
    </Tooltip>
  );
}

function useIsWorkspaceActive(workspace: MergedWorkspace) {
  const currentWorkspace = useCurrentWorkspace();
  const activeApp = useActiveBundle();
  return isWorkspaceActive(workspace, currentWorkspace, activeApp);
}

function WorkspaceDropdownWorkspace(props: {
  workspace: MergedWorkspace;
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
}) {
  const { workspace, onWorkspaceLinkClick } = props;
  const currentWorkspace = useCurrentWorkspace();
  const topLevelTransition: TargetTransition = {
    url: workspaceUrlFromCurrentPath(workspace.id, window.location.pathname),
    options: {
      shouldFadeOut: workspace.id !== currentWorkspace?.id,
      shouldUnrenderOutlet: false,
    },
  };

  return (
    <a
      href={topLevelTransition.url}
      className={styles.workspaceItemLink}
      onClick={(event) => {
        onWorkspaceLinkClick(event, topLevelTransition.options);
      }}
    >
      <div className={styles.workspaceItemNameContainer}>
        {!isEmptyString(workspace.color) ? <WorkspaceDot color={workspace.color} /> : null}
        <Truncated className={styles.workspaceItemName} aria-label={`${workspace.name} workspace`}>
          {workspace.name}
        </Truncated>
      </div>
    </a>
  );
}

function WorkspaceStaggerItem(props: {
  workspace: MergedWorkspace;
  index: number;
  shouldReduceMotion: boolean | null;
  hasToggled: boolean;
  isSampleWorkspace: boolean;
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
  currentWorkspaceRef: Ref<HTMLLIElement> | null;
}) {
  const isActive = useIsWorkspaceActive(props.workspace);

  return (
    <m.li
      className={`${styles.staggerItem} ${styles.workspaceItem}`}
      aria-current={isActive}
      ref={props.currentWorkspaceRef}
      initial={props.hasToggled ? entranceFadeUpInitial(props.shouldReduceMotion) : false}
      animate={ENTRANCE_FADE_UP_ANIMATE}
      transition={props.hasToggled ? itemStaggerTransition(props.index, props.shouldReduceMotion) : undefined}
    >
      {props.isSampleWorkspace ? (
        <SampleWorkspaceItem />
      ) : (
        <WorkspaceDropdownWorkspace workspace={props.workspace} onWorkspaceLinkClick={props.onWorkspaceLinkClick} />
      )}
    </m.li>
  );
}

function AccountListItemContent(props: { isExpired: boolean; session: Session }) {
  // session.name is the login's home-account name (see SwitchAccountButton).
  const providerLabel = formatAuthProvider(props.session.auth_provider);
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap="100" w="full">
      <Box minW="0">
        <Truncated display="block" text="product-p-active">
          {props.session.email}
        </Truncated>
        <Truncated display="block" text="product-pxs" color="subtle">
          home: {props.session.name}
        </Truncated>
      </Box>
      {/* Right padding clears the absolutely-positioned log-out button. */}
      <Box flexShrink="0" mr="400">
        {props.isExpired ? (
          <Label color="grey" showStatusIndicator={true}>
            Signed out
          </Label>
        ) : providerLabel != null ? (
          <Label color="grey">{providerLabel}</Label>
        ) : null}
      </Box>
    </Box>
  );
}

function AccountListItem(props: {
  session: Session;
  onSwitchAccount: (sessionId: number) => unknown;
  onLogoutAccount: (sessionId: number) => unknown;
  isLoggingOut: boolean;
}) {
  const { session, onSwitchAccount, onLogoutAccount, isLoggingOut } = props;
  const isExpired = isSessionExpired(session);
  const sharedProps = {
    flexGrow: '1',
    minW: '0',
    px: '200',
    py: '150',
    textAlign: 'left',
  } as const;

  return (
    <>
      {isExpired ? (
        <PlainLink
          {...sharedProps}
          href="/login?addAccount=true"
          isExternal
          withSafeExternalAttributes={false}
          aria-label={`${session.name} account (expired)`}
          display="block"
        >
          <AccountListItemContent isExpired={isExpired} session={session} />
        </PlainLink>
      ) : (
        <PlainButton
          {...sharedProps}
          onClick={() => onSwitchAccount(session.id)}
          aria-label={`${session.name} account`}
        >
          <AccountListItemContent isExpired={isExpired} session={session} />
        </PlainButton>
      )}
      <Button
        variant="subtle"
        className={styles.sessionItemLogoutButton}
        onClick={() => onLogoutAccount(session.id)}
        isDisabled={isLoggingOut}
        aria-label={`Log out of ${session.name} account`}
        icon="delete-trash"
        isIconOnly={true}
        size="sm"
      />
    </>
  );
}

function SwitchAccountButton(props: { onClick: () => void; accountName?: string }) {
  const { isSwitchingAccounts } = use(AccountSwitcherContext);
  const currentSession = useStore(currentSessionStore);

  if (currentSession == null) {
    return null;
  }

  // In membership mode the active account can differ from the login's home
  // account, so prefer the resolved active-account name when one is supplied;
  // the email stays the login identity's email.
  const displayName = props.accountName ?? currentSession.name;

  return (
    <PlainButton
      onClick={props.onClick}
      display="flex"
      w="full"
      justifyContent="space-between"
      alignItems="center"
      textAlign="left"
      px="200"
      py="150"
      className={styles.accountSwitcherButton}
      aria-expanded={isSwitchingAccounts}
      aria-controls={isSwitchingAccounts ? 'account-switcher-accounts-list' : undefined}
      aria-label="Switch accounts"
    >
      <Box>
        <Text display="block" text="product-p-active">
          {displayName}
        </Text>
        <Text display="block" text="product-pxs-secondary">
          {currentSession.email}
        </Text>
      </Box>
      <Icon className={styles.accountSwitcherButtonIcon} name="chevron-right" />
    </PlainButton>
  );
}

function MembershipListItem(props: {
  accountId: number;
  accountName: string;
  logoUrl?: string;
  isHome: boolean;
  isCurrent: boolean;
  onSwitch: (accountId: number) => unknown;
}) {
  return (
    <PlainButton
      flexGrow="1"
      minW="0"
      px="200"
      py="150"
      textAlign="left"
      onClick={() => props.onSwitch(props.accountId)}
      aria-label={`Switch to ${props.accountName}`}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="100" w="full">
        <Box display="flex" alignItems="center" gap="150" minW="0">
          {/* Logo when the account has one, otherwise a colored initials avatar
              keyed off the name — gives each account a stable visual marker.
              logo_url follows Account.logo_url, which is '' (not absent) when
              there's no logo; pass undefined so Avatar falls back to initials
              instead of rendering an empty <img>. */}
          <Avatar
            src={!isEmptyString(props.logoUrl) ? props.logoUrl : undefined}
            name={props.accountName}
            autoColor={true}
            size="s"
          />
          <Truncated display="block" text="product-p-active">
            {props.accountName}
          </Truncated>
        </Box>
        <Box display="flex" alignItems="center" gap="100" flexShrink="0">
          {props.isHome ? <Label color="grey">home</Label> : null}
          {props.isCurrent ? (
            <Text text="product-pxs" color="accent">
              current
            </Text>
          ) : null}
        </Box>
      </Box>
    </PlainButton>
  );
}

export function WorkspaceDropdown(props: {
  onWorkspaceLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
}) {
  const auth = useAuth();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [direction, setDirection] = useState<1 | -1>(1);
  const [hasToggled, setHasToggled] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const workspaceCountQuery = useWorkspaceCount();
  const { isSampleWorkspace } = useSampleWorkspace();
  const filteredWorkspacesQuery = useFilterWorkspacesByName(searchQuery, {
    includeSampleWorkspace: true,
  });

  const { isSwitchingAccounts, setIsSwitchingAccounts } = use(AccountSwitcherContext);
  const currentWorkspace = useCurrentWorkspace();

  const canSwitchAccounts = !auth.account.is_masq;
  const sessions = useStore(sessionsStore);
  const currentSession = useStore(currentSessionStore);
  const snackbars = useSnackbars();

  // Accounts the current login belongs to, switched via the X-Active-Account-Id
  // header (same session cookie) rather than by login.
  const membershipsQuery = useAccountMemberships();
  const rawMemberships = membershipsQuery.data ?? [];

  const decoupleEnabled = Boolean(auth.account.feature_flags?.[DECOUPLE_USERS_FLAG]);
  const activeAccountId = auth.account.id;
  // The current login's home account. home_account_id matches the isHome check in
  // the list below; fall back to the auth user's account when a persisted session
  // predates that field.
  const homeAccountId = currentSession?.home_account_id ?? auth.user?.account_id;

  // The backend's memberships list can omit the home account (e.g. a backfilled
  // login with no account_users row) — which leaves a user who switched into
  // another account with no row to click to get back. Ensure home is always
  // present. Prefer the home-account name from the session, but still inject the
  // row with a generic label if the name is missing — the way back matters more
  // than the label (and the row is tagged "home" regardless).
  const memberships = useMemo(() => {
    if (homeAccountId == null || rawMemberships.some((membership) => membership.account_id === homeAccountId)) {
      return rawMemberships;
    }
    const homeName = currentSession?.name;
    const accountName = homeName == null || homeName === '' ? 'Your account' : homeName;
    // logo_url: '' — the synthesized fallback row has no logo source, so it
    // falls back to the initials avatar (and keeps the row type uniform with the
    // API memberships so logo_url is always present).
    return [{ account_id: homeAccountId, account_name: accountName, logo_url: '' }, ...rawMemberships];
  }, [rawMemberships, homeAccountId, currentSession?.name]);

  // decouple_users is evaluated per active account, so when the user has switched
  // into another account (active != home) it can read false there — which would
  // hide the account list and strand them with no way back. Keep the list
  // available whenever they're away from home, regardless of that account's flag.
  const inSwitchedAccount = homeAccountId != null && homeAccountId !== activeAccountId;
  // There's a point to showing the account list only if it offers somewhere to go
  // — an account other than the one that's currently active. This subsumes the old
  // "more than one account" check (when active is in the list) but also handles a
  // switched account whose only other row is home (e.g. the backend returned just
  // the current account, or nothing and we injected home), where a plain length > 1
  // gate would hide the list and strand the user.
  const hasSwitchTarget = memberships.some((membership) => membership.account_id !== activeAccountId);
  // Switching is available when the flag is on OR the user is already away from
  // home. The settled-fetch (isSuccess) gate applies only to the home case — it
  // avoids flipping the panel mid-fetch there — but must NOT apply when switched
  // away: the synthesized home row lets the user get back even if the memberships
  // fetch is still loading or failed, and hiding it would strand them.
  const canSwitchMemberships = inSwitchedAccount || (decoupleEnabled && membershipsQuery.isSuccess);
  // A count-only gate isn't safe: a backfilled login can hold rows while the flag
  // is off, which is why switching is gated on the flag / being away from home above.
  const useMembershipSwitching = canSwitchAccounts && canSwitchMemberships && hasSwitchTarget;

  const filteredSessions = useMemo(() => {
    if (isEmptyString(searchQuery)) {
      return sessions;
    }

    const lowerQuery = searchQuery.toLowerCase();
    return sessions.filter((session) => `${session.name}|${session.email}`.toLowerCase().includes(lowerQuery));
  }, [searchQuery, sessions]);

  const filteredMemberships = useMemo(() => {
    if (isEmptyString(searchQuery)) {
      return memberships;
    }

    const lowerQuery = searchQuery.toLowerCase();
    return memberships.filter((membership) => membership.account_name.toLowerCase().includes(lowerQuery));
  }, [searchQuery, memberships]);

  const allWorkspacesCount = workspaceCountQuery.data ?? 0;
  const workspaceData = filteredWorkspacesQuery.data ?? [];

  const accountCount = useMembershipSwitching ? memberships.length + sessions.length : sessions.length;
  const shouldShowSearch = isSwitchingAccounts ? accountCount > 3 : allWorkspacesCount > 3;

  // In two-list mode each section labels itself ("Accounts" / "Logged in as"),
  // so the single panel-level label is suppressed to avoid duplication.
  const showPanelLabel = !(isSwitchingAccounts && useMembershipSwitching);

  const currentWorkspaceElementRef = useRef<HTMLLIElement | null>(null);

  useMountEffect(() => {
    if (currentWorkspaceElementRef.current == null) {
      return;
    }

    currentWorkspaceElementRef.current.scrollIntoView({
      block: 'center',
    });
  });

  const { track } = useAnalytics();

  const handleToggleAccounts = () => {
    setHasToggled(true);
    setDirection(isSwitchingAccounts ? -1 : 1);
    if (!isSwitchingAccounts) {
      void track('multiAccount', 'viewedAccounts', { 'Session Count': sessions.length });
    }
    setIsSwitchingAccounts(!isSwitchingAccounts);
    setSearchQuery('');
  };

  const logoutSessionMutation = useHydraUIAPIMutation((variables: { id: number }) =>
    hydraUiApi.sessions.logout({ params: { path: { id: String(variables.id) } } }),
  );

  const handleLogoutAccount = async (sessionId: number) => {
    const targetSession = sessions.find((s) => s.id === sessionId);
    if (targetSession == null) {
      return;
    }

    try {
      await logoutSessionMutation.mutateAsync({ id: sessionId });
    } catch {
      snackbars.create({
        id: 'multi-account.account.logout.error',
        message: "Couldn't log out of that account. Please try again",
        isError: true,
      });
      return;
    }

    void track('multiAccount', 'loggedOutAccount', {
      'Session Count': sessions.length,
      'Logged-Out Account Name': targetSession.name,
      'Was Current': sessionId === currentSession?.id,
    });

    const remaining = sessions.filter((session) => session.id !== sessionId);
    updateSessions(remaining);

    if (sessionId !== currentSession?.id) {
      // Logging out an account this tab isn't viewing. `sessionId` differs from
      // our active slot, so this only broadcasts — peer tab(s) on that slot
      // self-invalidate and redirect to login, while this tab is untouched.
      authSession.invalidate(sessionId);
      return;
    }

    if (remaining.length === 0) {
      // Last account: a full `/logout` already invalidates this slot and
      // notifies same-slot peers, so no separate `invalidate(sessionId)` here.
      navigateExternal('/logout');
      return;
    }

    // Switch to the next available session (lowest index in what remains).
    const next = remaining[0];
    changeToSession(next.id);
    // Switch first so `invalidate(sessionId)` targets a slot that is no longer
    // our active one — this tab moves to `next` instead of redirecting to
    // login, while peer tab(s) still on the logged-out slot redirect.
    authSession.invalidate(sessionId);
    setIsSwitchingAccounts(false);
    navigateExternal('/');
  };

  const handleSwitchToAccount = (accountId: number) => {
    if (accountId === activeAccountId) {
      setDirection(-1);
      setIsSwitchingAccounts(false);
      return;
    }

    const target = memberships.find((membership) => membership.account_id === accountId);

    void track('multiAccount', 'switchedAccounts', {
      'Session Count': memberships.length,
      'Current Account ID': activeAccountId,
      'New Account Name': target?.account_name ?? '',
    });

    switchAccount(accountId);
  };

  const handleSwitchAccount = (sessionId: number) => {
    if (sessionId === currentSession?.id) {
      setDirection(-1);
      setIsSwitchingAccounts(false);
      return;
    }

    // Should not happen in the real-world
    const targetSession = sessions.find((s) => s.id === sessionId);
    if (targetSession == null) {
      setDirection(-1);
      setIsSwitchingAccounts(false);
      return;
    }

    void track('multiAccount', 'switchedAccounts', {
      'Session Count': sessions.length,
      'Current Account ID': currentSession?.id,
      'New Account Name': targetSession.name,
    });

    // To handle any errors after the workspace switch (e.g. the session expired
    // and we didn't know) we want to be able to show an informative snackbar to
    // the user and allow them to log in again.
    sessionStorage.setItem(
      PENDING_SESSION_SWITCH_KEY,
      JSON.stringify({ id: targetSession.id, name: targetSession.name }),
    );
    changeToSession(sessionId);
    setIsSwitchingAccounts(false);
    // Navigate to the dashboard for the new account. Here we don't want the
    // workspace or account ID in the URL, and we also want the page to fully
    // load, rather than navigate internally, so that fresh data is loaded.
    navigateExternal('/');
  };

  return (
    <PopoverContent className={styles.content}>
      {canSwitchAccounts ? (
        <SwitchAccountButton
          onClick={handleToggleAccounts}
          accountName={useMembershipSwitching ? auth.account.name : undefined}
        />
      ) : null}
      <Box px="200" py="200">
        {shouldShowSearch ? (
          <Search
            value={searchQuery}
            onSearchInput={setSearchQuery}
            onSearch={setSearchQuery}
            shouldSearchOnClear={true}
            shouldSearchOnInput={true}
            placeholder={isSwitchingAccounts ? 'Find an account…' : 'Find a workspace…'}
            autoFocus={true}
            label={isSwitchingAccounts ? 'Accounts' : 'Workspaces'}
            w="full"
            maxW="full"
          />
        ) : showPanelLabel ? (
          <TextLabel>{isSwitchingAccounts ? 'Accounts' : 'Workspaces'}</TextLabel>
        ) : null}
      </Box>
      <m.div className={styles.panelWrapper}>
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          {isSwitchingAccounts ? (
            <m.section
              key="accounts"
              custom={direction}
              variants={panelVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {/*
                Two distinct dimensions live side by side (gated on the flag):
                "Accounts" switches the active account for this login via the
                X-Active-Account-Id header (no re-auth); "Logged in as" switches
                between separate logins/identities, each with its own session
                cookie. When the flag is off, only the logins list shows — the
                pre-decoupling behavior.
              */}
              {useMembershipSwitching ? (
                <>
                  <Box px="200" pt="100" pb="050">
                    <TextLabel>Accounts</TextLabel>
                  </Box>
                  <section className={styles.workspaceList}>
                    <List id="account-switcher-accounts-list" styleType="none" gap="0">
                      {filteredMemberships.length > 0 ? (
                        filteredMemberships.map((membership, index) => (
                          <m.li
                            key={membership.account_id}
                            className={styles.sessionItem}
                            aria-current={membership.account_id === activeAccountId}
                            initial={hasToggled ? entranceFadeUpInitial(shouldReduceMotion) : false}
                            animate={ENTRANCE_FADE_UP_ANIMATE}
                            transition={hasToggled ? itemStaggerTransition(index, shouldReduceMotion) : undefined}
                          >
                            <MembershipListItem
                              accountId={membership.account_id}
                              accountName={membership.account_name}
                              logoUrl={membership.logo_url}
                              isHome={membership.account_id === homeAccountId}
                              isCurrent={membership.account_id === activeAccountId}
                              onSwitch={handleSwitchToAccount}
                            />
                          </m.li>
                        ))
                      ) : (
                        <ListItem>
                          <TextLabel m="200" variant="secondary">
                            No results found
                          </TextLabel>
                        </ListItem>
                      )}
                    </List>
                  </section>
                  <Box px="200" pt="100" pb="050">
                    <TextLabel>Logged in as</TextLabel>
                  </Box>
                </>
              ) : null}
              <section className={styles.workspaceList}>
                <List
                  id={useMembershipSwitching ? undefined : 'account-switcher-accounts-list'}
                  styleType="none"
                  gap="0"
                >
                  {filteredSessions.length > 0 ? (
                    filteredSessions.map((session, index) => (
                      <m.li
                        key={session.email}
                        className={styles.sessionItem}
                        aria-current={currentSession?.id === session.id}
                        initial={hasToggled ? entranceFadeUpInitial(shouldReduceMotion) : false}
                        animate={ENTRANCE_FADE_UP_ANIMATE}
                        transition={hasToggled ? itemStaggerTransition(index, shouldReduceMotion) : undefined}
                      >
                        <AccountListItem
                          session={session}
                          onSwitchAccount={handleSwitchAccount}
                          onLogoutAccount={handleLogoutAccount}
                          isLoggingOut={
                            logoutSessionMutation.isPending && logoutSessionMutation.variables?.id === session.id
                          }
                        />
                      </m.li>
                    ))
                  ) : (
                    <ListItem>
                      <TextLabel m="200" variant="secondary">
                        No results found
                      </TextLabel>
                    </ListItem>
                  )}
                </List>
              </section>
              <Box as="section" display="flex" alignItems="center" py="150" px="200">
                <Button
                  flexGrow="1"
                  isDisabled={sessions.length >= MAX_ACCOUNTS}
                  // Navigating internally to the login page causes issues with
                  // form field tracking. Until we can figure out why, we're
                  // going to do a full reload. Not ideal, but a temporary solution
                  href="/login?addAccount=true"
                  isExternal={true}
                  withSafeExternalAttributes={false}
                  onClick={() => {
                    void track('multiAccount', 'clickedAddAccount', { 'Session Count': sessions.length });
                  }}
                >
                  Add another account
                </Button>
              </Box>
            </m.section>
          ) : (
            <m.div
              key="workspaces"
              custom={direction}
              variants={panelVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <section className={styles.workspaceList}>
                <List styleType="none" gap="0">
                  {workspaceData.length > 0 ? (
                    workspaceData.map((w: MergedWorkspace, index: number) => (
                      <WorkspaceStaggerItem
                        key={w.id}
                        workspace={w}
                        index={index}
                        shouldReduceMotion={shouldReduceMotion}
                        hasToggled={hasToggled}
                        isSampleWorkspace={isSampleWorkspace(w.cio_workspace_id)}
                        onWorkspaceLinkClick={props.onWorkspaceLinkClick}
                        currentWorkspaceRef={w.id === currentWorkspace?.id ? currentWorkspaceElementRef : null}
                      />
                    ))
                  ) : (
                    <ListItem>
                      <TextLabel m="200" variant="secondary">
                        No results found
                      </TextLabel>
                    </ListItem>
                  )}
                </List>
              </section>
              <Box as="section" display="flex" alignItems="center" py="150" px="200">
                <Button
                  href="/settings/workspaces"
                  flexGrow="1"
                  onClick={(event) => {
                    props.onWorkspaceLinkClick(event as React.MouseEvent<HTMLAnchorElement>);
                  }}
                >
                  Manage all workspaces
                </Button>
              </Box>
            </m.div>
          )}
        </AnimatePresence>
      </m.div>
    </PopoverContent>
  );
}
